import { Alert, Button, ConfirmDialog, EmptyState, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { api, apiBase } from '../../lib/api.ts'
import { TAMANHO_MAXIMO_DO_FUNDO, urlDoFundo } from '../kanban/fundo-de-quadro.ts'
import '../kanban/fundo-de-quadro.css'

/**
 * Biblioteca GLOBAL de fundos de quadro, no Console
 * (SPEC-20261027-1840-biblioteca-de-fundos-e-imagem-do-quadro).
 *
 * O super admin sobe aqui as imagens que todo mundo vai poder escolher ao
 * criar um quadro (botão "Imagem" das cores). Tudo no R2, inclusive as que
 * vêm com o Orbind (a API as envia ao subir). Remover uma pronta só a tira da
 * escolha; quem já usa continua com ela.
 */
export function FundosDeQuadroSection() {
  const toast = useToast()
  const qc = useQueryClient()
  const arquivo = useRef<HTMLInputElement>(null)
  const [remover, setRemover] = useState<{ id: string; name: string; pronto: boolean } | null>(null)

  const lista = useQuery({
    queryKey: ['console', 'background-library'],
    queryFn: async () => (await api.console['background-library'].$get()).json(),
  })
  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['console', 'background-library'] })
    void qc.invalidateQueries({ queryKey: ['kanbans', 'background-library'] })
  }

  const enviar = useMutation({
    mutationFn: async (file: File) => {
      const corpo = new FormData()
      corpo.append('file', file)
      corpo.append('name', file.name.replace(/\.[^.]+$/, ''))
      const r = await fetch(`${apiBase()}/console/background-library`, {
        method: 'POST',
        headers: { authorization: `Bearer ${localStorage.getItem('orbind.token') ?? ''}` },
        body: corpo,
      })
      if (!r.ok) {
        const erro = (await r.json().catch(() => null)) as { error?: { message?: string } } | null
        throw new Error(erro?.error?.message ?? 'Não foi possível enviar a imagem.')
      }
    },
    onSuccess: () => {
      recarregar()
      toast.push({ tone: 'success', message: 'Imagem adicionada à biblioteca.' })
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao enviar.' }),
  })

  const apagar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.console['background-library'][':id'].$delete({ param: { id } })
      if (!r.ok) throw new Error('Não foi possível remover a imagem.')
    },
    onSuccess: () => {
      recarregar()
      setRemover(null)
      toast.push({ tone: 'success', message: 'Imagem removida da biblioteca.' })
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao remover.' }),
  })

  const imagens = lista.data?.imagens ?? []

  return (
    <>
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Fundos de quadro</h1>
          <p className="adm-head__desc">
            Imagens que qualquer pessoa pode escolher como fundo ao criar um quadro. Valem para toda a instalação.
          </p>
        </div>
        <Button icon="fas fa-upload" loading={enviar.isPending} onClick={() => arquivo.current?.click()}>
          Enviar imagem
        </Button>
        <input
          ref={arquivo}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            e.target.value = ''
            if (!f) return
            if (!f.type.startsWith('image/')) {
              toast.push({ tone: 'danger', message: 'Escolha um arquivo de imagem.' })
              return
            }
            if (f.size > TAMANHO_MAXIMO_DO_FUNDO) {
              toast.push({ tone: 'danger', message: 'Imagem grande demais: o limite é 8 MB.' })
              return
            }
            enviar.mutate(f)
          }}
        />
      </div>

      <div className="cons-panel">
        <h2 className="cons-panel__title">Biblioteca</h2>
        {lista.isPending ? (
          <LoadingState variant="list" label="Carregando a biblioteca" />
        ) : lista.isError ? (
          <Alert tone="danger" title="Não foi possível carregar a biblioteca">
            Verifique a conexão e tente de novo.
          </Alert>
        ) : imagens.length === 0 ? (
          <EmptyState
            icon="far fa-image"
            title="Nenhuma imagem na biblioteca"
            description="As imagens enviadas aqui aparecem para todos no botão Imagem, ao criar um quadro."
          />
        ) : (
          <div className="kbimg__grade kbimg__grade--console">
            {imagens.map((img) => (
              <figure key={img.id} className="kbimg__cartao">
                <div className="kbimg__miniatura" style={{ backgroundImage: `url("${urlDoFundo(img.url)}")` }} aria-hidden />
                <figcaption>
                  <span title={img.name}>{img.name}</span>
                  {img.pronto ? <span className="kbimg__selo">vem com o Orbind</span> : null}
                  <button
                    type="button"
                    className="kbimg__remover"
                    aria-label={`Remover ${img.name} da biblioteca`}
                    title="Remover da biblioteca"
                    onClick={() => setRemover({ id: img.id, name: img.name, pronto: img.pronto })}
                  >
                    <i className="fas fa-trash" aria-hidden />
                  </button>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={remover !== null}
        title="Remover da biblioteca?"
        description={
          remover?.pronto
            ? `"${remover.name}" deixa de aparecer para escolha. Quadros que já usam este fundo continuam com ele.`
            : `"${remover?.name ?? ''}" deixa de aparecer para escolha. Quadros que já usam esta imagem ficam sem fundo.`
        }
        confirmLabel="Remover"
        destructive
        loading={apagar.isPending}
        onConfirm={() => remover && apagar.mutate(remover.id)}
        onCancel={() => setRemover(null)}
      />
    </>
  )
}
